import { z } from 'zod';
import { booleanish, cents, optionalCents, optionalText, requiredText, paginationQuery, hexColor } from './common';

// ---------------------------------------------------------------- Categorias
export const categoryInputSchema = z.object({
  name: requiredText(60, 'Informe o nome da categoria'),
  color: hexColor.nullish(),
  showInCatalog: z.boolean().default(true),
  isActive: z.boolean().default(true),
});
export type CategoryInput = z.infer<typeof categoryInputSchema>;

export const reorderSchema = z.object({ ids: z.array(z.string().uuid()).min(1).max(500) });

// ---------------------------------------------------------------- Variações
export const productOptionInputSchema = z.object({
  name: requiredText(30, 'Nome da variação'),
  values: z.array(requiredText(40)).min(1, 'Adicione pelo menos um valor').max(50),
});

export const productVariantInputSchema = z.object({
  id: z.string().uuid().optional(),
  name: requiredText(120),
  options: z.record(z.string(), z.string()).default({}),
  sku: optionalText(60),
  barcode: optionalText(60),
  priceCents: optionalCents,
  costCents: optionalCents,
  stock: z.number().min(-1_000_000).max(1_000_000).optional(),
  minStock: z.number().min(0).max(1_000_000).nullish(),
  isActive: z.boolean().default(true),
});
export type ProductVariantInput = z.infer<typeof productVariantInputSchema>;

export const variationTemplateSchema = z.object({
  name: requiredText(40),
  values: z.array(requiredText(40)).min(1).max(50),
});

// ---------------------------------------------------------------- Produto
export const productInputSchema = z.object({
  name: requiredText(120, 'Informe o nome do produto'),
  priceCents: cents,
  promoPriceCents: optionalCents,
  costCents: optionalCents,
  description: optionalText(5000),
  categoryId: z.string().uuid().nullish(),
  code: optionalText(60),
  barcode: optionalText(60),
  unit: z.string().max(10).default('un'),
  isFeatured: z.boolean().default(false),
  isActive: z.boolean().default(true),
  showInCatalog: z.boolean().default(true),
  manageStock: z.boolean().default(false),
  /** Estoque inicial (apenas na criação; depois use movimentações). */
  initialStock: z.number().min(0).max(1_000_000).optional(),
  minStock: z.number().min(0).max(1_000_000).nullish(),
  weightGrams: z.number().int().min(0).max(1_000_000).nullish(),
  /** Medidas da embalagem em cm (peso cúbico do frete). null = usa a embalagem padrão da loja. */
  lengthCm: z.number().min(0).max(1000).nullish(),
  widthCm: z.number().min(0).max(1000).nullish(),
  heightCm: z.number().min(0).max(1000).nullish(),
  /** Fornecedor principal (recurso "purchases"). */
  supplierId: z.string().uuid().nullish(),
  tags: z.array(z.string().trim().max(30)).max(20).default([]),
  options: z.array(productOptionInputSchema).max(3).default([]),
  variants: z.array(productVariantInputSchema).max(300).default([]),
  /** Ids de Media já enviados (ordem = ordem das fotos). */
  imageMediaIds: z.array(z.string().uuid()).max(10).optional(),
  aiGenerated: z.boolean().optional(),
});
export type ProductInput = z.infer<typeof productInputSchema>;

export const productUpdateSchema = productInputSchema.omit({ initialStock: true }).partial();
export type ProductUpdateInput = z.infer<typeof productUpdateSchema>;

export const productListQuery = paginationQuery.extend({
  q: z.string().trim().max(100).optional(),
  categoryId: z.string().uuid().optional(),
  featured: booleanish.optional(),
  active: booleanish.optional(),
  stock: z.enum(['all', 'low', 'out', 'managed', 'unmanaged']).default('all'),
  /**
   * Cadastro incompleto (links dos alertas): cost = ativo sem custo (no produto ou em alguma
   * variação ativa); photo = ativo, visível no catálogo e sem foto.
   */
  missing: z.enum(['cost', 'photo']).optional(),
  sort: z.enum(['name', 'recent', 'price_asc', 'price_desc', 'best_sellers', 'stock_asc', 'stock_desc']).default('name'),
  pageSize: z.coerce.number().int().min(1).max(500).default(60),
});
export type ProductListQuery = z.infer<typeof productListQuery>;

// ---------------------------------------------------------------- Estoque
export const stockMovementInputSchema = z.object({
  variantId: z.string().uuid().nullish(),
  /** loss = perda ou avaria (sai do estoque como a saída, mas aparece separada nos relatórios). */
  type: z.enum(['entry', 'exit', 'adjustment', 'loss']),
  /** entry/exit/loss: quantidade positiva. adjustment: novo saldo absoluto. */
  quantity: z.number().min(0).max(1_000_000),
  unitCostCents: optionalCents,
  reason: optionalText(200),
});
export type StockMovementInput = z.infer<typeof stockMovementInputSchema>;

export const stockSettingsSchema = z.object({
  manageStock: z.boolean().optional(),
  /** null limpa o mínimo; ausente mantém o atual. */
  minStock: z.number().min(0).max(1_000_000).nullish(),
});

export const stockListQuery = paginationQuery.extend({
  q: z.string().trim().max(100).optional(),
  filter: z.enum(['all', 'in_stock', 'low', 'out', 'unmanaged']).default('all'),
  sort: z.enum(['name', 'stock_asc', 'stock_desc', 'value_desc']).default('name'),
  pageSize: z.coerce.number().int().min(1).max(500).default(100),
});

// ---------------------------------------------------------------- IA
export const aiProductSuggestionSchema = z.object({
  /** Media id de uma foto já enviada (opcional). */
  mediaId: z.string().uuid().optional(),
  /** Texto livre: "legging preta cintura alta tam P ao GG" */
  hint: z.string().trim().max(500).optional(),
  /** Quando já existe nome/categoria e só queremos a descrição. */
  name: z.string().trim().max(120).optional(),
  categoryName: z.string().trim().max(60).optional(),
  mode: z.enum(['full', 'description']).default('full'),
});
export type AiProductSuggestionInput = z.infer<typeof aiProductSuggestionSchema>;

export const productImportRowSchema = z.object({
  name: requiredText(120),
  priceCents: cents,
  costCents: optionalCents,
  code: optionalText(60),
  barcode: optionalText(60),
  category: optionalText(60),
  stock: z.number().min(0).max(1_000_000).nullish(),
  description: optionalText(5000),
});
export const productImportSchema = z.object({ rows: z.array(productImportRowSchema).min(1).max(2000) });
